import { FloatingFormItem, InputDecimal } from '@/components/_common';
import { InvoiceType } from '@/core/domain/invoice/invoices/invoices.enums';
import { useConsultInvoiceHub } from '@/core/domain/invoiceTransmissionHub/Invoice/Invoice.hooks';
import { Modal, Table, Radio, Alert } from 'antd';
import { useCallback, useEffect, useState } from 'react';

interface Props {
  opened: boolean;
  onClose: VoidFunction;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataError: any;
  invoiceId: number;
  transmit: (newNumber: number) => Promise<void>;
}

export default function DuplicateInvoiceErrorModal(props: Props) {
  const [invoiceNumber, setInvoiceNumber] = useState<number>();
  const [errorType, setErrorType] = useState<0 | 1>(0);

  const invoiceConsultNfe = useConsultInvoiceHub();

  useEffect(() => {
    if (props.dataError) {
      setInvoiceNumber(Number(props.dataError?.number) + 1);
    }
  }, [props.dataError]);

  const handleTransmit = useCallback(async () => {
    if (invoiceNumber) {
      await props.transmit(invoiceNumber);
    }
  }, [props, invoiceNumber]);

  const handleConsult = useCallback(async () => {
    try {
      await invoiceConsultNfe.mutateAsync({
        invoiceId: props.invoiceId,
        duplicateKey: props.dataError.key,
        type: InvoiceType.NFE,
      });

      handleClose();
    } catch {}
  }, [props]);

  const handleClose = () => {
    setInvoiceNumber(undefined);
    setErrorType(0);
    props.onClose();
  };

  return (
    <Modal
      width="60%"
      open={props.opened}
      onCancel={handleClose}
      title={props.dataError?.message}
      okText={errorType === 0 ? 'Atualizar nota' : 'Transmitir nota'}
      cancelText="Fechar"
      onOk={errorType === 0 ? handleConsult : handleTransmit}
    >
      <>
        <p>
          Foi encontrada na SEFAZ uma nota transmitida com a mesma numeração.
        </p>

        <Table
          dataSource={[props.dataError]}
          columns={[
            {
              dataIndex: 'number',
              title: 'Numeração',
            },
            {
              dataIndex: 'serie',
              title: 'Série',
            },
            {
              dataIndex: 'key',
              title: 'Chave',
            },
          ]}
          pagination={false}
          scroll={{ x: true }}
        />

        <Radio.Group
          style={{ marginTop: 24 }}
          buttonStyle="solid"
          value={errorType}
          onChange={({ target }) => setErrorType(target.value)}
        >
          <Radio.Button value={0}>Corrigir Duplicidade da NF-e</Radio.Button>
          <Radio.Button value={1}>Transmitir com nova numeração</Radio.Button>
        </Radio.Group>

        {errorType === 0 && (
          <Alert
            style={{ marginTop: 24 }}
            type="warning"
            description="ATENÇÃO! Verifique se a nota fiscal no sistema coincide com a registrada na SEFAZ. Caso haja divergência, será necessário ajustar manualmente a nota no sistema para que corresponda à da SEFAZ. Ao corrigir a duplicidade, o sistema atualizará apenas o status da nota e a chave de acesso para corresponder à da SEFAZ."
          />
        )}

        {errorType === 1 && (
          <>
            <Alert
              style={{ marginTop: 24, marginBottom: 24 }}
              type="warning"
              description="ATENÇÃO! A numeração das notas fiscais deve ser sequencial, sem saltos. Transmitir com uma nova numeração aplicará o novo número informado à nota."
            />

            <FloatingFormItem label="Numeração" initialValue={invoiceNumber}>
              <InputDecimal
                fractions={0}
                value={invoiceNumber || undefined}
                onChange={(v) => {
                  setInvoiceNumber(v);
                }}
              />
            </FloatingFormItem>
          </>
        )}
      </>
    </Modal>
  );
}
